import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import BulkImportDrawer from "../../components/BulkImportDrawer";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import useDevOverride from "../../hooks/useDevOverride";
import {
  listTaskMasters,
  createTaskMaster,
  updateTaskMaster,
  deleteTaskMaster,
  exportTaskMasters,
  bulkImportTaskMasters,
} from "../../api/taskMasterService";
import { listTaskGroups } from "../../api/taskGroupService";
import { paginatedRows } from "../../utils/pagination";
import { showApiError, showToast } from "../../utils/toast";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// 0 = Sunday … 6 = Saturday — must match Carbon::dayOfWeek on the backend.
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function TaskMastersPage() {
  const devOverride = useDevOverride();
  const [tab, setTab] = useState("all");
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit' | 'view', task }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const masters = useApi(() => listTaskMasters({ per_page: 100 }));
  const groups = useApi(() => listTaskGroups({ per_page: 100 }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTaskMasters();
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  const allRows = paginatedRows(masters.data);
  const groupRows = paginatedRows(groups.data);

  const onDelete = async (t) => {
    if (!window.confirm(`Delete task master "${t.name}"?\n\nThis won't affect existing employee tasks already generated from it.`)) return;
    try {
      await deleteTaskMaster(t.id);
      showToast("success", "Task master deleted");
      masters.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const filtered = allRows.filter((t) => {
    if (tab === "all") return true;
    if (tab === "oneoff") return !t.is_recurring;
    return t.is_recurring && t.recurrence_pattern === tab;
  });

  const recLabel = (t) => {
    if (!t.is_recurring) {
      const d = t.non_recurring_due_in_days;
      return d == null ? "One-off" : `One-off · due in ${d} day${d === 1 ? "" : "s"}`;
    }
    if (t.recurrence_pattern === "daily") return `Daily · ${t.daily_deadline || ""}`;
    if (t.recurrence_pattern === "weekly")
      return `Weekly · ${WEEKDAYS[t.weekly_deadline_day ?? 0]} · reminder ${WEEKDAYS[t.weekly_reminder_day ?? 0]}`;
    if (t.recurrence_pattern === "monthly")
      return `Monthly · day ${t.monthly_deadline_day} · reminder day ${t.monthly_reminder_day}`;
    if (t.recurrence_pattern === "quarterly") {
      const parts = [1, 2, 3, 4]
        .map((q) => {
          const mo = t[`quarterly_${q}_deadline_month`];
          const day = t[`quarterly_${q}_deadline_day`];
          return mo && day ? `${MONTHS[mo - 1]} ${day}` : null;
        })
        .filter(Boolean);
      return `Quarterly · ${parts.join(" / ")}`;
    }
    if (t.recurrence_pattern === "half_yearly") {
      const m1 = MONTHS[(t.half_yearly_1_deadline_month || 1) - 1];
      const m2 = MONTHS[(t.half_yearly_2_deadline_month || 7) - 1];
      return `Half-yearly · ${m1} ${t.half_yearly_1_deadline_day} & ${m2} ${t.half_yearly_2_deadline_day}`;
    }
    if (t.recurrence_pattern === "yearly") {
      const m = MONTHS[(t.yearly_deadline_month || 1) - 1];
      return `Yearly · ${m} ${t.yearly_deadline_day}`;
    }
    return "—";
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Task Masters</h1>
          <div className="page-sub">{allRows.length} templates · attach to businesses to auto-generate task instances</div>
        </div>
        <div className="page-actions">
          {devOverride && (
            <>
              <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
                <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export"}
              </button>
              <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
                <Icon name="upload" size={14} /> Bulk import
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <Icon name="plus" size={14} /> New task master
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: "all", label: "All" },
          { id: "weekly", label: "Weekly" },
          { id: "monthly", label: "Monthly" },
          { id: "quarterly", label: "Quarterly" },
          { id: "half_yearly", label: "Half-yearly" },
          { id: "yearly", label: "Yearly" },
          { id: "daily", label: "Daily" },
          { id: "oneoff", label: "One-off" },
        ].map((f) => (
          <button key={f.id} className={`chip ${tab === f.id ? "active" : ""}`} onClick={() => setTab(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {masters.loading ? (
        <LoadingBlock rows={2} height={140} />
      ) : filtered.length === 0 ? (
        <EmptyState icon="layers" title="No task masters in this view" hint="Create your first reusable task template." />
      ) : (
        <GroupedTaskMasters
          tasks={filtered}
          groups={groupRows}
          recLabel={recLabel}
          onView={(t) => setDrawer({ mode: "view", task: t })}
          onEdit={(t) => setDrawer({ mode: "edit", task: t })}
          onDelete={onDelete}
        />
      )}

      <TaskMasterDrawer
        open={!!drawer}
        mode={drawer?.mode}
        task={drawer?.task}
        groups={groupRows}
        recLabel={recLabel}
        onClose={() => setDrawer(null)}
        onSwitchToEdit={() => setDrawer((d) => d ? { ...d, mode: "edit" } : d)}
        onSaved={() => {
          setDrawer(null);
          masters.reload();
        }}
      />

      {devOverride && (
        <BulkImportDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onImported={() => {
            masters.reload();
            groups.reload();
          }}
          title="Bulk import task masters"
          subtitle="Export current data, edit in Excel, then upload. Rows with an existing id are updated; blank-id rows create new templates."
          instructions="Step 1 — Download the current data. Step 2 — Edit/add rows (leave id blank to create new). Use either task_group_id or task_group_name to point at a group. Step 3 — Upload."
          downloadLabel="Download current data"
          downloadFn={exportTaskMasters}
          importFn={bulkImportTaskMasters}
        />
      )}
    </>
  );
}

function GroupedTaskMasters({ tasks, groups, recLabel, onView, onEdit, onDelete }) {
  // Group task masters by task_group_id; preserve groups order then append "Ungrouped".
  const byGroup = new Map();
  for (const g of groups) byGroup.set(g.id, []);
  const ungrouped = [];
  for (const t of tasks) {
    const gid = t.task_group_id ?? null;
    if (gid != null && byGroup.has(gid)) byGroup.get(gid).push(t);
    else ungrouped.push(t);
  }

  const sections = [];
  for (const g of groups) {
    const list = byGroup.get(g.id) || [];
    if (list.length === 0) continue;
    sections.push({ id: g.id, name: g.name, tasks: list });
  }
  if (ungrouped.length > 0) {
    sections.push({ id: "_ungrouped", name: "Ungrouped", tasks: ungrouped });
  }

  return (
    <div className="col gap-lg">
      {sections.map((sec) => (
        <div key={sec.id} className="card" style={{ background: "var(--surface-2)" }}>
          <div className="card-head" style={{ background: "var(--surface-2)" }}>
            <div className="row gap-sm">
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "var(--primary-50)",
                  color: "var(--primary-700)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="folder" size={16} />
              </div>
              <div>
                <div className="card-title">{sec.name}</div>
                <div className="card-sub">
                  {sec.tasks.length} task master{sec.tasks.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          </div>
          <div
            className="card-body"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 12,
            }}
          >
            {sec.tasks.map((t) => (
              <TaskMasterCard
                key={t.id}
                task={t}
                recLabel={recLabel}
                onView={() => onView?.(t)}
                onEdit={() => onEdit?.(t)}
                onDelete={() => onDelete?.(t)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TaskMasterCard({ task, recLabel, onView, onEdit, onDelete }) {
  const patternLabel = task.is_recurring
    ? task.recurrence_pattern?.replace("_", "-") || "recurring"
    : "one-off";

  return (
    <div
      className="card clickable"
      style={{
        background: "var(--surface)",
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: "pointer",
      }}
      onClick={onView}
    >
      <div className="row gap-sm">
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "var(--primary-50)",
            color: "var(--primary-700)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon name="layers" size={14} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-primary fw-600" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {task.name}
          </div>
          <div className="t-muted text-xs" style={{ marginTop: 2 }}>
            {recLabel(task)}
          </div>
        </div>
        <div className="row gap-sm" onClick={(e) => e.stopPropagation()}>
          <button className="icon-btn" title="Edit" onClick={onEdit}>
            <Icon name="edit" size={13} />
          </button>
          <button
            className="icon-btn"
            title="Delete"
            onClick={onDelete}
            style={{ color: "var(--danger)" }}
          >
            <Icon name="trash" size={13} />
          </button>
        </div>
      </div>
      <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
        <span className={`badge ${task.is_recurring ? "badge-info" : "badge-neutral"}`}>
          {patternLabel}
        </span>
        {task.requires_everyday_reminder && (
          <span className="badge badge-warning">Daily reminder</span>
        )}
      </div>
    </div>
  );
}

function TaskMasterDrawer({ open, mode, task, groups, recLabel, onClose, onSwitchToEdit, onSaved }) {
  const [form, setForm] = useState(initialForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Hydrate form whenever the drawer opens or the task identity changes.
  useEffect(() => {
    if (!open) return;
    if ((mode === "edit" || mode === "view") && task) {
      setForm(formFromTask(task));
    } else {
      setForm(initialForm());
    }
    setErrors({});
  }, [open, mode, task]);

  const isView = mode === "view";

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (mode === "edit") {
        await updateTaskMaster(task.id, payload);
        showToast("success", "Task master updated");
      } else {
        await createTaskMaster(payload);
        showToast("success", "Task master created");
        setForm(initialForm());
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const errFor = (k) => errors[k]?.[0];

  if (isView && task) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={task.name}
        subtitle={recLabel?.(task)}
        wide
        footer={
          <>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-primary" onClick={onSwitchToEdit}>
              <Icon name="edit" size={13} /> Edit
            </button>
          </>
        }
      >
        <TaskMasterReadView task={task} groups={groups} recLabel={recLabel} />
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${task?.name || "task master"}` : "New task master"}
      subtitle="Recurrence pattern · attach to businesses afterwards"
      wide
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.name}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <Field label="Task name" error={errFor("name")}>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Task group" error={errFor("task_group_id")}>
          <SearchableSelect
            value={form.task_group_id}
            onChange={(v) => setForm({ ...form, task_group_id: v ?? "" })}
            options={groups.map((g) => ({ id: g.id, label: g.name }))}
            placeholder="— Select —"
            searchPlaceholder="Search groups…"
            width="100%"
          />
        </Field>

        <Field label="Recurrence">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
            {[
              { v: "daily", label: "Daily", icon: "clock" },
              { v: "weekly", label: "Weekly", icon: "calendar" },
              { v: "monthly", label: "Monthly", icon: "calendar" },
              { v: "quarterly", label: "Quarterly", icon: "calendar" },
              { v: "half_yearly", label: "Half-yearly", icon: "calendar" },
              { v: "yearly", label: "Yearly", icon: "calendar" },
              { v: "oneoff", label: "One-off", icon: "flag" },
            ].map((p) => {
              const selected = p.v === "oneoff" ? !form.is_recurring : form.is_recurring && form.recurrence_pattern === p.v;
              return (
                <button
                  key={p.v}
                  type="button"
                  className={`btn ${selected ? "btn-primary" : "btn-secondary"}`}
                  style={{ flexDirection: "column", padding: "12px 8px", gap: 4 }}
                  onClick={() =>
                    setForm({
                      ...form,
                      is_recurring: p.v !== "oneoff",
                      recurrence_pattern: p.v === "oneoff" ? null : p.v,
                    })
                  }
                >
                  <Icon name={p.icon} size={16} /> {p.label}
                </button>
              );
            })}
          </div>
        </Field>

        {form.is_recurring && form.recurrence_pattern === "daily" && (
          <Field label="Daily deadline time (HH:mm:ss)" error={errFor("daily_deadline")}>
            <input
              type="time"
              className="input"
              step="1"
              value={form.daily_deadline}
              onChange={(e) => setForm({ ...form, daily_deadline: e.target.value })}
            />
          </Field>
        )}

        {form.is_recurring && form.recurrence_pattern === "weekly" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Deadline day of week" error={errFor("weekly_deadline_day")}>
              <WeekdaySelect
                value={form.weekly_deadline_day}
                onChange={(v) => setForm({ ...form, weekly_deadline_day: v })}
              />
            </Field>
            <Field label="Reminder day of week" error={errFor("weekly_reminder_day")}>
              <WeekdaySelect
                value={form.weekly_reminder_day}
                onChange={(v) => setForm({ ...form, weekly_reminder_day: v })}
              />
            </Field>
          </div>
        )}

        {form.is_recurring && form.recurrence_pattern === "monthly" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Deadline day of month" error={errFor("monthly_deadline_day")}>
              <input
                type="number"
                className="input"
                min="1"
                max="31"
                value={form.monthly_deadline_day}
                onChange={(e) => setForm({ ...form, monthly_deadline_day: +e.target.value })}
              />
            </Field>
            <Field label="Reminder day of month" error={errFor("monthly_reminder_day")}>
              <input
                type="number"
                className="input"
                min="1"
                max="31"
                value={form.monthly_reminder_day}
                onChange={(e) => setForm({ ...form, monthly_reminder_day: +e.target.value })}
              />
            </Field>
          </div>
        )}

        {form.is_recurring && form.recurrence_pattern === "half_yearly" && (
          <div className="col gap-md">
            <div className="card-sub">First half deadline + reminder</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <Field label="Deadline month" error={errFor("half_yearly_1_deadline_month")}>
                <MonthSelect
                  value={form.half_yearly_1_deadline_month}
                  onChange={(v) => setForm({ ...form, half_yearly_1_deadline_month: v })}
                />
              </Field>
              <Field label="Deadline day" error={errFor("half_yearly_1_deadline_day")}>
                <DayInput
                  value={form.half_yearly_1_deadline_day}
                  month={form.half_yearly_1_deadline_month}
                  onChange={(v) => setForm({ ...form, half_yearly_1_deadline_day: v })}
                />
              </Field>
              <Field label="Reminder month" error={errFor("half_yearly_1_reminder_month")}>
                <MonthSelect
                  value={form.half_yearly_1_reminder_month}
                  onChange={(v) => setForm({ ...form, half_yearly_1_reminder_month: v })}
                />
              </Field>
              <Field label="Reminder day" error={errFor("half_yearly_1_reminder_day")}>
                <DayInput
                  value={form.half_yearly_1_reminder_day}
                  month={form.half_yearly_1_reminder_month}
                  onChange={(v) => setForm({ ...form, half_yearly_1_reminder_day: v })}
                />
              </Field>
            </div>
            <div className="card-sub">Second half deadline + reminder</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <Field label="Deadline month" error={errFor("half_yearly_2_deadline_month")}>
                <MonthSelect
                  value={form.half_yearly_2_deadline_month}
                  onChange={(v) => setForm({ ...form, half_yearly_2_deadline_month: v })}
                />
              </Field>
              <Field label="Deadline day" error={errFor("half_yearly_2_deadline_day")}>
                <DayInput
                  value={form.half_yearly_2_deadline_day}
                  month={form.half_yearly_2_deadline_month}
                  onChange={(v) => setForm({ ...form, half_yearly_2_deadline_day: v })}
                />
              </Field>
              <Field label="Reminder month" error={errFor("half_yearly_2_reminder_month")}>
                <MonthSelect
                  value={form.half_yearly_2_reminder_month}
                  onChange={(v) => setForm({ ...form, half_yearly_2_reminder_month: v })}
                />
              </Field>
              <Field label="Reminder day" error={errFor("half_yearly_2_reminder_day")}>
                <DayInput
                  value={form.half_yearly_2_reminder_day}
                  month={form.half_yearly_2_reminder_month}
                  onChange={(v) => setForm({ ...form, half_yearly_2_reminder_day: v })}
                />
              </Field>
            </div>
          </div>
        )}

        {form.is_recurring && form.recurrence_pattern === "quarterly" && (
          <div className="col gap-md">
            {[1, 2, 3, 4].map((q) => (
              <div key={q} className="col gap-sm">
                <div className="card-sub">{["First", "Second", "Third", "Fourth"][q - 1]} quarter — deadline + reminder</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                  <Field label="Deadline month" error={errFor(`quarterly_${q}_deadline_month`)}>
                    <MonthSelect
                      value={form[`quarterly_${q}_deadline_month`]}
                      onChange={(v) => setForm({ ...form, [`quarterly_${q}_deadline_month`]: v })}
                    />
                  </Field>
                  <Field label="Deadline day" error={errFor(`quarterly_${q}_deadline_day`)}>
                    <DayInput
                      value={form[`quarterly_${q}_deadline_day`]}
                      month={form[`quarterly_${q}_deadline_month`]}
                      onChange={(v) => setForm({ ...form, [`quarterly_${q}_deadline_day`]: v })}
                    />
                  </Field>
                  <Field label="Reminder month" error={errFor(`quarterly_${q}_reminder_month`)}>
                    <MonthSelect
                      value={form[`quarterly_${q}_reminder_month`]}
                      onChange={(v) => setForm({ ...form, [`quarterly_${q}_reminder_month`]: v })}
                    />
                  </Field>
                  <Field label="Reminder day" error={errFor(`quarterly_${q}_reminder_day`)}>
                    <DayInput
                      value={form[`quarterly_${q}_reminder_day`]}
                      month={form[`quarterly_${q}_reminder_month`]}
                      onChange={(v) => setForm({ ...form, [`quarterly_${q}_reminder_day`]: v })}
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}

        {form.is_recurring && form.recurrence_pattern === "yearly" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <Field label="Deadline month" error={errFor("yearly_deadline_month")}>
              <MonthSelect
                value={form.yearly_deadline_month}
                onChange={(v) => setForm({ ...form, yearly_deadline_month: v })}
              />
            </Field>
            <Field label="Deadline day" error={errFor("yearly_deadline_day")}>
              <DayInput
                value={form.yearly_deadline_day}
                month={form.yearly_deadline_month}
                onChange={(v) => setForm({ ...form, yearly_deadline_day: v })}
              />
            </Field>
            <Field label="Reminder month" error={errFor("yearly_reminder_month")}>
              <MonthSelect
                value={form.yearly_reminder_month}
                onChange={(v) => setForm({ ...form, yearly_reminder_month: v })}
              />
            </Field>
            <Field label="Reminder day" error={errFor("yearly_reminder_day")}>
              <DayInput
                value={form.yearly_reminder_day}
                month={form.yearly_reminder_month}
                onChange={(v) => setForm({ ...form, yearly_reminder_day: v })}
              />
            </Field>
          </div>
        )}

        {!form.is_recurring && (
          <Field label="Due in (days)" error={errFor("non_recurring_due_in_days")}>
            <input
              type="number"
              min="0"
              className="input"
              value={form.non_recurring_due_in_days}
              onChange={(e) => setForm({ ...form, non_recurring_due_in_days: e.target.value })}
              placeholder="e.g. 7"
            />
            <div className="field-hint">
              When attached to a client, the task is due this many days from the attach date.
            </div>
          </Field>
        )}

        <div className="row gap-sm">
          <input
            type="checkbox"
            id="every"
            checked={form.requires_everyday_reminder}
            onChange={(e) => setForm({ ...form, requires_everyday_reminder: e.target.checked })}
          />
          <label htmlFor="every" className="field-label" style={{ marginBottom: 0 }}>
            Send every-day reminder until completed
          </label>
        </div>
      </div>
    </Drawer>
  );
}

function MonthSelect({ value, onChange }) {
  return (
    <select className="select" value={value || ""} onChange={(e) => onChange(+e.target.value)}>
      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
    </select>
  );
}

function WeekdaySelect({ value, onChange }) {
  // Sunday is 0; accept both 0 and null/undefined gracefully. We coerce via
  // a separate "no value" placeholder so 0 (Sunday) is selectable.
  const v = value === 0 || value ? String(value) : "";
  return (
    <select className="select" value={v} onChange={(e) => onChange(+e.target.value)}>
      <option value="" disabled>— Pick a weekday —</option>
      {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
    </select>
  );
}

// Conservative day count per month. February is fixed at 28 so we never
// schedule for Feb 29 (which silently disappears in non-leap years).
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function daysInMonth(month) {
  if (!month || month < 1 || month > 12) return 31;
  return DAYS_IN_MONTH[month - 1];
}

function DayInput({ value, onChange, month }) {
  const max = daysInMonth(month);

  // If the month picker changes such that the current day is no longer valid
  // (e.g. switched from Jul to Jun while day was 31), clamp it down.
  useEffect(() => {
    if (value && value > max) onChange(max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max]);

  return (
    <input
      type="number"
      className="input"
      min={1}
      max={max}
      value={value || ""}
      onChange={(e) => {
        const n = +e.target.value;
        if (!e.target.value) { onChange(""); return; }
        if (Number.isNaN(n)) return;
        onChange(Math.min(Math.max(n, 1), max));
      }}
    />
  );
}

function Field({ label, error, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function TaskMasterReadView({ task, groups, recLabel }) {
  const groupName = groups.find((g) => g.id === task.task_group_id)?.name || "—";
  return (
    <div className="col gap-md">
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <KV k="Name" v={task.name} />
          <KV k="Group" v={groupName} />
          <KV k="Recurrence" v={recLabel?.(task) || "—"} />
          <KV k="Daily reminder until done" v={task.requires_everyday_reminder ? "Yes" : "No"} />
        </div>
      </div>
      {task.is_recurring && task.recurrence_pattern === "monthly" && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Monthly schedule</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <KV k="Deadline day" v={task.monthly_deadline_day} />
            <KV k="Reminder day" v={task.monthly_reminder_day} />
          </div>
        </div>
      )}
      {task.is_recurring && task.recurrence_pattern === "yearly" && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Yearly schedule</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <KV k="Deadline" v={`${MONTHS[(task.yearly_deadline_month || 1) - 1]} ${task.yearly_deadline_day}`} />
            <KV k="Reminder" v={`${MONTHS[(task.yearly_reminder_month || 1) - 1]} ${task.yearly_reminder_day}`} />
          </div>
        </div>
      )}
      {task.is_recurring && task.recurrence_pattern === "half_yearly" && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Half-yearly schedule</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <KV k="First deadline" v={`${MONTHS[(task.half_yearly_1_deadline_month || 1) - 1]} ${task.half_yearly_1_deadline_day}`} />
            <KV k="First reminder" v={`${MONTHS[(task.half_yearly_1_reminder_month || 1) - 1]} ${task.half_yearly_1_reminder_day}`} />
            <KV k="Second deadline" v={`${MONTHS[(task.half_yearly_2_deadline_month || 1) - 1]} ${task.half_yearly_2_deadline_day}`} />
            <KV k="Second reminder" v={`${MONTHS[(task.half_yearly_2_reminder_month || 1) - 1]} ${task.half_yearly_2_reminder_day}`} />
          </div>
        </div>
      )}
      {task.is_recurring && task.recurrence_pattern === "quarterly" && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Quarterly schedule</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {[1, 2, 3, 4].map((q) => (
              <div key={q} style={{ display: "contents" }}>
                <KV
                  k={`Q${q} deadline`}
                  v={task[`quarterly_${q}_deadline_month`]
                    ? `${MONTHS[(task[`quarterly_${q}_deadline_month`] || 1) - 1]} ${task[`quarterly_${q}_deadline_day`]}`
                    : "—"}
                />
                <KV
                  k={`Q${q} reminder`}
                  v={task[`quarterly_${q}_reminder_month`]
                    ? `${MONTHS[(task[`quarterly_${q}_reminder_month`] || 1) - 1]} ${task[`quarterly_${q}_reminder_day`]}`
                    : "—"}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {task.is_recurring && task.recurrence_pattern === "daily" && (
        <div className="card" style={{ padding: 14 }}>
          <KV k="Daily deadline" v={task.daily_deadline} />
        </div>
      )}
      {task.is_recurring && task.recurrence_pattern === "weekly" && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Weekly schedule</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <KV k="Deadline weekday" v={task.weekly_deadline_day != null ? WEEKDAYS[task.weekly_deadline_day] : "—"} />
            <KV k="Reminder weekday" v={task.weekly_reminder_day != null ? WEEKDAYS[task.weekly_reminder_day] : "—"} />
          </div>
        </div>
      )}
      {!task.is_recurring && (
        <div className="card" style={{ padding: 14 }}>
          <KV
            k="Due in (days)"
            v={task.non_recurring_due_in_days == null ? "—" : `${task.non_recurring_due_in_days} days`}
          />
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 500 }}>{k}</div>
      <div style={{ fontSize: 13, color: "var(--ink-900)", marginTop: 2 }}>{v ?? "—"}</div>
    </div>
  );
}

function formFromTask(t) {
  const base = initialForm();
  return {
    ...base,
    name: t.name || "",
    task_group_id: t.task_group_id ? String(t.task_group_id) : "",
    is_recurring: !!t.is_recurring,
    recurrence_pattern: t.recurrence_pattern || base.recurrence_pattern,
    requires_everyday_reminder: !!t.requires_everyday_reminder,

    daily_deadline: t.daily_deadline || base.daily_deadline,

    weekly_deadline_day: t.weekly_deadline_day ?? base.weekly_deadline_day,
    weekly_reminder_day: t.weekly_reminder_day ?? base.weekly_reminder_day,

    monthly_deadline_day: t.monthly_deadline_day ?? base.monthly_deadline_day,
    monthly_reminder_day: t.monthly_reminder_day ?? base.monthly_reminder_day,

    half_yearly_1_deadline_month: t.half_yearly_1_deadline_month ?? base.half_yearly_1_deadline_month,
    half_yearly_1_deadline_day: t.half_yearly_1_deadline_day ?? base.half_yearly_1_deadline_day,
    half_yearly_1_reminder_month: t.half_yearly_1_reminder_month ?? base.half_yearly_1_reminder_month,
    half_yearly_1_reminder_day: t.half_yearly_1_reminder_day ?? base.half_yearly_1_reminder_day,
    half_yearly_2_deadline_month: t.half_yearly_2_deadline_month ?? base.half_yearly_2_deadline_month,
    half_yearly_2_deadline_day: t.half_yearly_2_deadline_day ?? base.half_yearly_2_deadline_day,
    half_yearly_2_reminder_month: t.half_yearly_2_reminder_month ?? base.half_yearly_2_reminder_month,
    half_yearly_2_reminder_day: t.half_yearly_2_reminder_day ?? base.half_yearly_2_reminder_day,

    quarterly_1_deadline_month: t.quarterly_1_deadline_month ?? base.quarterly_1_deadline_month,
    quarterly_1_deadline_day: t.quarterly_1_deadline_day ?? base.quarterly_1_deadline_day,
    quarterly_1_reminder_month: t.quarterly_1_reminder_month ?? base.quarterly_1_reminder_month,
    quarterly_1_reminder_day: t.quarterly_1_reminder_day ?? base.quarterly_1_reminder_day,
    quarterly_2_deadline_month: t.quarterly_2_deadline_month ?? base.quarterly_2_deadline_month,
    quarterly_2_deadline_day: t.quarterly_2_deadline_day ?? base.quarterly_2_deadline_day,
    quarterly_2_reminder_month: t.quarterly_2_reminder_month ?? base.quarterly_2_reminder_month,
    quarterly_2_reminder_day: t.quarterly_2_reminder_day ?? base.quarterly_2_reminder_day,
    quarterly_3_deadline_month: t.quarterly_3_deadline_month ?? base.quarterly_3_deadline_month,
    quarterly_3_deadline_day: t.quarterly_3_deadline_day ?? base.quarterly_3_deadline_day,
    quarterly_3_reminder_month: t.quarterly_3_reminder_month ?? base.quarterly_3_reminder_month,
    quarterly_3_reminder_day: t.quarterly_3_reminder_day ?? base.quarterly_3_reminder_day,
    quarterly_4_deadline_month: t.quarterly_4_deadline_month ?? base.quarterly_4_deadline_month,
    quarterly_4_deadline_day: t.quarterly_4_deadline_day ?? base.quarterly_4_deadline_day,
    quarterly_4_reminder_month: t.quarterly_4_reminder_month ?? base.quarterly_4_reminder_month,
    quarterly_4_reminder_day: t.quarterly_4_reminder_day ?? base.quarterly_4_reminder_day,

    yearly_deadline_month: t.yearly_deadline_month ?? base.yearly_deadline_month,
    yearly_deadline_day: t.yearly_deadline_day ?? base.yearly_deadline_day,
    yearly_reminder_month: t.yearly_reminder_month ?? base.yearly_reminder_month,
    yearly_reminder_day: t.yearly_reminder_day ?? base.yearly_reminder_day,

    non_recurring_due_in_days: t.non_recurring_due_in_days == null ? "" : String(t.non_recurring_due_in_days),
  };
}

function initialForm() {
  return {
    name: "",
    task_group_id: "",
    is_recurring: true,
    recurrence_pattern: "monthly",
    requires_everyday_reminder: false,

    daily_deadline: "18:00:00",

    // Default: due Monday with a Friday reminder (3 business days lead time).
    weekly_deadline_day: 1,
    weekly_reminder_day: 5,

    monthly_deadline_day: 20,
    monthly_reminder_day: 15,

    half_yearly_1_deadline_month: 9,
    half_yearly_1_deadline_day: 30,
    half_yearly_1_reminder_month: 9,
    half_yearly_1_reminder_day: 20,
    half_yearly_2_deadline_month: 3,
    half_yearly_2_deadline_day: 31,
    half_yearly_2_reminder_month: 3,
    half_yearly_2_reminder_day: 20,

    // Quarterly defaults: end-of-quarter deadlines (Jun/Sep/Dec/Mar) with
    // reminders 10 days before. Admin can edit any of these in the drawer.
    quarterly_1_deadline_month: 6, quarterly_1_deadline_day: 30,
    quarterly_1_reminder_month: 6, quarterly_1_reminder_day: 20,
    quarterly_2_deadline_month: 9, quarterly_2_deadline_day: 30,
    quarterly_2_reminder_month: 9, quarterly_2_reminder_day: 20,
    quarterly_3_deadline_month: 12, quarterly_3_deadline_day: 31,
    quarterly_3_reminder_month: 12, quarterly_3_reminder_day: 20,
    quarterly_4_deadline_month: 3, quarterly_4_deadline_day: 31,
    quarterly_4_reminder_month: 3, quarterly_4_reminder_day: 20,

    yearly_deadline_month: 7,
    yearly_deadline_day: 31,
    yearly_reminder_month: 7,
    yearly_reminder_day: 20,

    non_recurring_due_in_days: "",
  };
}

function buildPayload(form) {
  const base = {
    name: form.name,
    is_recurring: form.is_recurring,
    requires_everyday_reminder: form.requires_everyday_reminder,
  };
  if (form.task_group_id) base.task_group_id = Number(form.task_group_id);

  if (!form.is_recurring) {
    return {
      ...base,
      non_recurring_due_in_days:
        form.non_recurring_due_in_days === "" ? null : Number(form.non_recurring_due_in_days),
    };
  }

  base.recurrence_pattern = form.recurrence_pattern;

  if (form.recurrence_pattern === "daily") {
    return { ...base, daily_deadline: form.daily_deadline };
  }
  if (form.recurrence_pattern === "weekly") {
    return {
      ...base,
      weekly_deadline_day: form.weekly_deadline_day,
      weekly_reminder_day: form.weekly_reminder_day,
    };
  }
  if (form.recurrence_pattern === "monthly") {
    return {
      ...base,
      monthly_deadline_day: form.monthly_deadline_day,
      monthly_reminder_day: form.monthly_reminder_day,
    };
  }
  if (form.recurrence_pattern === "half_yearly") {
    return {
      ...base,
      half_yearly_1_deadline_month: form.half_yearly_1_deadline_month,
      half_yearly_1_deadline_day: form.half_yearly_1_deadline_day,
      half_yearly_1_reminder_month: form.half_yearly_1_reminder_month,
      half_yearly_1_reminder_day: form.half_yearly_1_reminder_day,
      half_yearly_2_deadline_month: form.half_yearly_2_deadline_month,
      half_yearly_2_deadline_day: form.half_yearly_2_deadline_day,
      half_yearly_2_reminder_month: form.half_yearly_2_reminder_month,
      half_yearly_2_reminder_day: form.half_yearly_2_reminder_day,
    };
  }
  if (form.recurrence_pattern === "quarterly") {
    const out = { ...base };
    for (const q of [1, 2, 3, 4]) {
      out[`quarterly_${q}_deadline_month`] = form[`quarterly_${q}_deadline_month`];
      out[`quarterly_${q}_deadline_day`] = form[`quarterly_${q}_deadline_day`];
      out[`quarterly_${q}_reminder_month`] = form[`quarterly_${q}_reminder_month`];
      out[`quarterly_${q}_reminder_day`] = form[`quarterly_${q}_reminder_day`];
    }
    return out;
  }
  if (form.recurrence_pattern === "yearly") {
    return {
      ...base,
      yearly_deadline_month: form.yearly_deadline_month,
      yearly_deadline_day: form.yearly_deadline_day,
      yearly_reminder_month: form.yearly_reminder_month,
      yearly_reminder_day: form.yearly_reminder_day,
    };
  }
  return base;
}
